import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { almacen, newOperationId, type Supply } from '../api';
import { Boton, Fallo, Pastilla, Rotulo } from '../componentes';
import { Escaner } from '../Escaner';
import { cantidad, soles } from '../formato';
import { IGV_COMPRAS, costoPorUnidadDeStock } from '../numeros';
import { segundoNumero } from '../equipos';
import {
  CONSULTA_DE_IMEI_OSIPTEL,
  ESTADOS_DEL_EQUIPO,
  agregarLectura,
  faltaSegundoImei,
  type EstadoDelEquipo,
} from '../serie';

/** Un equipo leído, con su costo propio si no es el de todos. */
interface Fila {
  serie: string;
  /** Vacío: el costo de la recepción. */
  costo: string;
  estado: EstadoDelEquipo;
  /**
   * El IMEI 2 o la serie de fábrica, si la caja trae otro (migración 274). El
   * cliente vuelve con el que tiene a mano, y la garantía lo busca por los dos.
   */
  segunda: string;
}

/**
 * Recibir lo que va por serie: un equipo por lectura (módulo `serials`,
 * docs/15 §2.41).
 *
 * Una cantidad no sirve aquí. Cada celular o laptop es uno, con su IMEI o su
 * serie impresa en la caja, y es lo que buscará la garantía y lo que la caja
 * venderá. Por eso se escanea cada caja, una lista se arma sola y la
 * cantidad que entra es cuántas series hay.
 *
 * **Costo y estado por equipo**: el reacondicionado no costó lo que el nuevo.
 * Cada fila toma el costo y el estado de la recepción si no se le pone otro.
 * Una serie que ya es de otro equipo del negocio la niega el servidor, y
 * entonces no entra ninguna: se quita y se reintenta con la misma operación.
 */
export function RecepcionPorSerie({
  insumo,
  guia,
  purchaseOrderId = null,
  alRecibir,
}: {
  insumo: Supply;
  guia: string;
  /**
   * La orden de compra contra la que se recibe, si se recibe desde ella. El
   * servidor ya la aceptaba; la orden se recibía solo por cantidad, y lo que
   * va por serie lo niega, así que un pedido de celulares no se cerraba nunca
   * (8 oct).
   */
  purchaseOrderId?: string | null;
  alRecibir?: () => void;
}) {
  const clientes = useQueryClient();
  const [operationId, setOperationId] = useState(newOperationId);
  const [filas, setFilas] = useState<Fila[]>([]);
  const [problema, setProblema] = useState<string | null>(null);
  const [otroModelo, setOtroModelo] = useState<string | null>(null);
  const [costo, setCosto] = useState('');
  const [conIgv, setConIgv] = useState(false);
  const [estado, setEstado] = useState<EstadoDelEquipo>('new');
  const [dosImei, setDosImei] = useState(false);

  /** El costo que viaja, sin IGV, de lo tecleado. Nulo si no es un número. */
  const sinIgv = (texto: string): string | null => costoPorUnidadDeStock(texto, { conIgv });

  const costoComun = sinIgv(costo);
  const costosDeFilas = filas.map((fila) => (fila.costo.trim() === '' ? costoComun : sinIgv(fila.costo)));
  const total = costosDeFilas.reduce<number | null>(
    (suma, uno) => (suma === null || uno === null ? null : suma + Number.parseFloat(uno)),
    0,
  );

  const segundas = filas.map((fila) => segundoNumero(fila.serie, fila.segunda));
  const problemaDeSegunda =
    faltaSegundoImei(filas, dosImei) ?? segundas.find((una) => una.problema !== null)?.problema ?? null;

  const recibir = useMutation({
    mutationFn: () =>
      almacen.receiveSerials({
        operationId,
        supplyId: insumo.id,
        unitCost: costoComun ?? '0',
        condition: estado,
        ...(guia.trim() === '' ? {} : { document: guia.trim() }),
        ...(purchaseOrderId === null ? {} : { purchaseOrderId }),
        units: filas.map((fila, indice) => ({
          serialNumber: fila.serie,
          ...(fila.costo.trim() === '' ? {} : { unitCost: costosDeFilas[indice]! }),
          ...(fila.estado === estado ? {} : { condition: fila.estado }),
          ...(segundas[indice]!.valor === null ? {} : { secondarySerialNumber: segundas[indice]!.valor }),
        })),
      }),
    onSuccess: () => {
      // Operación nueva y lista vacía: la siguiente caja es otra recepción.
      setOperationId(newOperationId());
      setFilas([]);
      setProblema(null);
      void clientes.invalidateQueries();
      alRecibir?.();
    },
  });

  const leer = (texto: string) => {
    const resultado = agregarLectura(filas, texto, dosImei);
    setProblema(resultado.problema);
    if (resultado.problema !== null) return;
    setOtroModelo(resultado.otroModelo ? resultado.equipos.at(-1)!.serie : null);
    setFilas(
      resultado.equipos.map((equipo, indice) => ({
        costo: '',
        estado,
        ...filas[indice],
        serie: equipo.serie,
        segunda: equipo.segunda,
      })),
    );
  };

  const cambiar = (indice: number, cambio: Partial<Fila>) =>
    setFilas(filas.map((fila, i) => (i === indice ? { ...fila, ...cambio } : fila)));

  const completo =
    filas.length > 0 &&
    costosDeFilas.every((uno) => uno !== null) &&
    problemaDeSegunda === null &&
    !recibir.isPending;

  return (
    <>
      <div className="campo">
        <Rotulo
          htmlFor="series"
          ayuda="Escanea la serie o el IMEI de cada caja, una por una. Si la etiqueta no se deja leer, tecléala y Enter."
        >
          Series que llegaron
        </Rotulo>
        <Escaner onCodigo={leer} etiqueta="Escanear la serie o el IMEI de cada equipo" />
        <button
          type="button"
          className={`opcion ${dosImei ? 'opcion--activa' : ''}`}
          aria-pressed={dosImei}
          // Solo con la lista vacía: a medias, la siguiente caja se emparejaba
          // con la anterior y todo quedaba corrido (auditoría del 12 oct).
          disabled={filas.length > 0}
          onClick={() => setDosImei(!dosImei)}
        >
          Dos IMEI por equipo
          <Pastilla
            texto={
              filas.length > 0
                ? 'se elige antes de leer la primera caja'
                : dosImei
                  ? 'lee los dos de cada caja, seguidos'
                  : 'un número por caja'
            }
            tono={dosImei ? 'aviso' : 'neutro'}
          />
        </button>
        <a className="enlace-sutil" href={CONSULTA_DE_IMEI_OSIPTEL} target="_blank" rel="noreferrer">
          Verificar IMEI en OSIPTEL («Checa tu IMEI»)
        </a>
        {problema === null ? null : (
          <p className="aviso-caja aviso-caja--peligro" role="alert">
            {problema}
          </p>
        )}
        {otroModelo === null || !filas.some((fila) => fila.serie === otroModelo) ? null : (
          <p className="aviso-caja" role="status">
            {otroModelo} parece de otro modelo que los anteriores: revisa que la caja sea {insumo.name}. Si no
            lo es, quítalo de la lista.
          </p>
        )}
      </div>

      <div className="formulario__fila">
        <div className="campo">
          <Rotulo
            htmlFor="costo-serie"
            ayuda="Lo que costó cada equipo. Si uno costó otra cosa (el reacondicionado), cámbialo en su fila."
          >
            Costo por equipo
          </Rotulo>
          <input
            id="costo-serie"
            inputMode="decimal"
            placeholder="899,00"
            value={costo}
            onChange={(e) => setCosto(e.target.value)}
          />
        </div>
        <div className="campo">
          <Rotulo htmlFor="estado-serie" ayuda="El estado de los equipos que llegan: nuevo, caja abierta, reacondicionado o usado. Uno distinto se cambia en su fila.">
            Estado
          </Rotulo>
          <select
            id="estado-serie"
            value={estado}
            onChange={(e) => setEstado(e.target.value as EstadoDelEquipo)}
          >
            {ESTADOS_DEL_EQUIPO.map((uno) => (
              <option key={uno.valor} value={uno.valor}>
                {uno.etiqueta}
              </option>
            ))}
          </select>
        </div>
      </div>

      <button
        type="button"
        className={`opcion ${conIgv ? 'opcion--activa' : ''}`}
        aria-pressed={conIgv}
        onClick={() => setConIgv(!conIgv)}
      >
        El precio incluye IGV ({IGV_COMPRAS} %)
        <Pastilla texto={conIgv ? 'se le quita al entrar' : 'ya va sin IGV'} tono={conIgv ? 'aviso' : 'neutro'} />
      </button>

      {filas.length === 0 ? (
        <p className="nota nota--pegada">
          Todavía no se leyó ninguna serie. Cada lectura es un {insumo.unit} de {insumo.name}.
        </p>
      ) : (
        <div className="tabla-envoltura">
          <table>
            <thead>
              <tr>
                <th>Serie</th>
                <th>IMEI 2 (opcional)</th>
                <th>Estado</th>
                <th className="numero">Costo, si es otro</th>
                <th aria-label="Quitar" />
              </tr>
            </thead>
            <tbody>
              {filas.map((fila, indice) => (
                <tr key={fila.serie}>
                  <td className="mono">{fila.serie}</td>
                  <td>
                    <input
                      aria-label={`IMEI 2 de ${fila.serie}`}
                      className="mono"
                      maxLength={60}
                      placeholder="si la caja trae otro"
                      value={fila.segunda}
                      onChange={(e) => cambiar(indice, { segunda: e.target.value })}
                    />
                  </td>
                  <td>
                    <select
                      aria-label={`Estado de ${fila.serie}`}
                      value={fila.estado}
                      onChange={(e) => cambiar(indice, { estado: e.target.value as EstadoDelEquipo })}
                    >
                      {ESTADOS_DEL_EQUIPO.map((uno) => (
                        <option key={uno.valor} value={uno.valor}>
                          {uno.etiqueta}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="numero">
                    <input
                      aria-label={`Costo de ${fila.serie}`}
                      inputMode="decimal"
                      placeholder={costo === '' ? 'el de arriba' : costo}
                      value={fila.costo}
                      onChange={(e) => cambiar(indice, { costo: e.target.value })}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="accion-fila"
                      onClick={() => setFilas(filas.filter((_, i) => i !== indice))}
                    >
                      Quitar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="nota nota--pegada">
        Entran <span className="mono">{cantidad(String(filas.length))}</span> {insumo.unit}
        {total === null || filas.length === 0 ? '' : ` por ${soles(total)} sin IGV`}.
      </p>

      {problemaDeSegunda === null ? null : (
        <p className="aviso-caja aviso-caja--peligro" role="alert">
          {problemaDeSegunda}
        </p>
      )}
      {recibir.isError ? <Fallo error={recibir.error} /> : null}
      {recibir.isSuccess ? (
        <p className="aviso-caja">
          Entraron {recibir.data.received} equipos con su serie. El saldo quedó en{' '}
          <span className="mono">{cantidad(recibir.data.balance)}</span>, y la caja ya puede venderlos.
        </p>
      ) : null}

      <Boton variante="primario" onClick={() => recibir.mutate()} disabled={!completo}>
        {recibir.isPending ? 'Ingresando…' : `Confirmar ${filas.length} ${filas.length === 1 ? 'equipo' : 'equipos'}`}
      </Boton>
    </>
  );
}
